// src/components/.../newplayer.component.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useDataLayer } from "../../../../Contexts/DataLayer.context";
import { usePlayer } from '../../../../Contexts/playerData.context';

/**
 * NewPlayerForm (hard-locked to positionArray keys)
 * Required: first, last, school, colPos1
 * Optional: nflDraftYear, cflDraftYear, jersey, impnip (Nationality)
 *
 * Position binding:
 *  - Label: POS_DESC
 *  - Value: POS_SPEC_CODE  -> saved in form.colPos1
 *  - Derived: Gen_Pos      -> saved in form.genPos
 */
const NewPlayerForm = ({ onCancel, onSaveSuccess }) => {
  const { schoolArray, positionArray } = useDataLayer();
  const { playerData, setPlayerData, setPlayerID, setActivePlayer } = usePlayer();

  const [form, setForm] = useState({
    first: "",
    last: "",
    school: "",
    colPos1: "",      // POS_SPEC_CODE
    genPos: "",       // from Gen_Pos
    nflDraftYear: "", // optional
    cflDraftYear: "", // optional
    jersey: "",       // optional
    impnip: "",       // optional (Nationality)
  });

  const [touched, setTouched] = useState({});

  const requiredOk = useMemo(() => {
    return (
      form.first.trim().length > 0 &&
      form.last.trim().length > 0 &&
      String(form.school).trim().length > 0 &&
      String(form.colPos1).trim().length > 0
    );
  }, [form]);

  const nflYearOk = useMemo(() => {
    if (!form.nflDraftYear) return true; // optional
    return /^[0-9]{4}$/.test(form.nflDraftYear);
  }, [form.nflDraftYear]);

  const cflYearOk = useMemo(() => {
    if (!form.cflDraftYear) return true; // optional
    return /^[0-9]{3,4}$/.test(form.cflDraftYear);
  }, [form.cflDraftYear]);

  const isFormValid = requiredOk && nflYearOk && cflYearOk;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Schools (expects: { schoolcode, school_short_name })
  const schoolOptions = useMemo(() => {
    return (schoolArray || []).map((s, idx) => ({
      value: String(s?.schoolcode ?? ""),
      label: String(s?.school_short_name ?? s?.schoolcode ?? ""),
      key: `${s?.schoolcode || "school"}-${idx}`,
    }));
  }, [schoolArray]);

  // Positions (HARD-LOCK: require POS_DESC, POS_SPEC_CODE, Gen_Pos)
  const positionOptions = useMemo(() => {
    return (positionArray || [])
      .map((p, idx) => {
        if (!p || typeof p.POS_DESC === "undefined" || typeof p.POS_SPEC_CODE === "undefined") {
          return null; // skip invalid
        }
        return {
          value: String(p.POS_SPEC_CODE), // POS_SPEC_CODE
          label: String(p.POS_DESC),      // POS_DESC
          genPos: typeof p.Gen_Pos === "undefined" ? "" : String(p.Gen_Pos),
          key: `pos-${idx}-${p.POS_SPEC_CODE}`,
        };
      })
      .filter(Boolean);
  }, [positionArray]);

  // When position changes: set colPos1 to POS_SPEC_CODE and genPos to Gen_Pos
  const handlePositionChange = (posSpecCode) => {
    const codeStr = String(posSpecCode);
    const match = (positionArray || []).find((p) => String(p?.POS_SPEC_CODE) === codeStr);
    const derivedGenPos = match && typeof match.Gen_Pos !== "undefined" ? String(match.Gen_Pos) : "";
    setForm((prev) => ({ ...prev, colPos1: codeStr, genPos: derivedGenPos }));
    setTouched((prev) => ({ ...prev, colPos1: true }));
  };

  const borderIfInvalid = (field) => {
    const isRequired = ["first", "last", "school", "colPos1"].includes(field);
    const touchedField = touched[field];
    const empty = !String(form[field] || "").trim();
    if (!isRequired) {
      // Optional year visual hints
      if (field === "nflDraftYear" && touchedField && form.nflDraftYear && !nflYearOk) {
        return "border border-danger border-2";
      }
      if (field === "cflDraftYear" && touchedField && form.cflDraftYear && !cflYearOk) {
        return "border border-danger border-2";
      }
      return "";
    }
    return touchedField && empty ? "border border-danger border-2" : "";
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    setTouched((prev) => ({
      ...prev,
      first: true,
      last: true,
      school: true,
      colPos1: true,
      nflDraftYear: prev.nflDraftYear || false,
      cflDraftYear: prev.cflDraftYear || false,
      jersey: prev.jersey || false,
      impnip: prev.impnip || false,
    }));
    if (!isFormValid) return;

    const payload = {
      first: form.first.trim(),
      last: form.last.trim(),
      school: String(form.school).trim(),
      colPos1: String(form.colPos1).trim(), // POS_SPEC_CODE
      genPos: String(form.genPos || "").trim(), // Gen_Pos
    };

    // Include optionals if present
    if (form.nflDraftYear) payload.nflDraftYear = form.nflDraftYear;
    if (form.cflDraftYear) payload.cflDraftYear = form.cflDraftYear;
    if (form.jersey) payload.jersey = form.jersey.trim();
    if (form.impnip) payload.impnip = form.impnip; // N/G/V3/V5


    try {


      const response = await fetch('https://www.fbxchange.com/ReactPHP/tomBox.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          script: 'createPlayer',
          payload: payload            // <-- payload key changed from `data` to `newPlayer`
        }),
      });

      const data = await response.json();
      const rec = data;

      // Prefer FBXID; if your backend doesn’t set one for College yet,
      // you can fall back to DR_Num (commented line shows how)
      const FBXID =
        rec.FBXID ?? rec.fbxid /* ?? rec.DR_Num */ ?? null;
      setPlayerID(FBXID);
      setActivePlayer(rec);

      // Build the row you want in state and set playerID
      const newRow = { ...rec, playerID: FBXID };

      // Insert at the TOP of the list
      setPlayerData(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return [newRow, ...safePrev];
      });

      if (typeof onSaveSuccess === 'function') {
        onSaveSuccess();
      }

    } catch (error) {
      console.error('createPlayer error:', error);
    }



    return;
    // Reset
    setForm({
      first: "",
      last: "",
      school: "",
      colPos1: "",
      genPos: "",
      nflDraftYear: "",
      cflDraftYear: "",
      jersey: "",
      impnip: "",
    });
    setTouched({});
  };

  // NEW: simple form reset handler for the Cancel button
  const handleReset = () => {
    setForm({
      first: "",
      last: "",
      school: "",
      colPos1: "",
      genPos: "",
      nflDraftYear: "",
      cflDraftYear: "",
      jersey: "",
      impnip: "",
    });
    setTouched({});
  };

  // Optional: warn if malformed position entries
  useEffect(() => {
    (positionArray || []).forEach((p, i) => {
      if (!p || typeof p.POS_DESC === "undefined" || typeof p.POS_SPEC_CODE === "undefined") {
        console.warn(`[NewPlayerForm] positionArray[${i}] missing POS_DESC/POS_SPEC_CODE. Skipped.`);
      }
    });
  }, [positionArray]);

  return (
    <form className="container-fluid" onSubmit={handleSubmit}>
      <div className="fw-bold text-center alert-warning rounded py-2 mb-2">
        Enter the required fields to create a new player
      </div>

      {/* Row: Names */}
      <div className="d-flex gap-1">
        <div className="col-24">
          <label className="fs-7 ps-1">
            Last Name<span className="text-danger ms-1">*</span>
          </label>
          <input
            type="text"
            className={`form-control fs-7 ps-1 ${borderIfInvalid("last")}`}
            placeholder="Last Name"
            value={form.last}
            onChange={(e) => handleChange("last", e.target.value)}
            onBlur={() => handleBlur("last")}
          />
        </div>
        <div className="col-24">
          <label className="fs-7 ps-1">
            First Name<span className="text-danger ms-1">*</span>
          </label>
          <input
            type="text"
            className={`form-control fs-7 ps-1 ${borderIfInvalid("first")}`}
            placeholder="First Name"
            value={form.first}
            onChange={(e) => handleChange("first", e.target.value)}
            onBlur={() => handleBlur("first")}
          />
        </div>
      </div>

      {/* Row: School + Position */}
      <div className="d-flex mt-2 gap-1 mb-3">
        <div className="col-24">
          <label className="fs-7 ps-1">
            School<span className="text-danger ms-1">*</span>
          </label>
          <select
            className={`form-select fs-7 ps-1 ${borderIfInvalid("school")}`}
            value={form.school}
            onChange={(e) => handleChange("school", e.target.value)}
            onBlur={() => handleBlur("school")}
          >
            <option value="">Select School</option>
            {schoolOptions.map((o) => (
              <option key={o.key} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="col-18">
          <label className="fs-7 ps-1">
            Position<span className="text-danger ms-1">*</span>
          </label>
          <select
            className={`form-select fs-7 ps-1 ${borderIfInvalid("colPos1")}`}
            value={form.colPos1} // POS_SPEC_CODE
            onChange={(e) => handlePositionChange(e.target.value)}
            onBlur={() => handleBlur("colPos1")}
          >
            <option value="">Select Position</option>
            {positionOptions.map((o) => (
              <option key={o.key} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-grow-1">
          <label className="fs-7">Gen Pos</label>
          <input
            type="text"
            className="form-control fs-7"
            value={form.genPos}
            readOnly
            placeholder="GP"
          />
        </div>
      </div>

      {/* Optional Box */}
      <div className="border rounded p-2 mt-2 alert-navy">
        <div className="fw-bold small mb-2">Optional</div>

        {/* Row: NFL + CFL Draft Years */}
        <div className="d-flex gap-1 mb-2">
          <div className="col-12">
            <label className="fs-7 ps-1">
              NFL Draft Year
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="NFL DY"
              className={`form-control fs-7 alert-info ps-1 ${borderIfInvalid("nflDraftYear")}`}
              value={form.nflDraftYear}
              onChange={(e) => handleChange("nflDraftYear", e.target.value.replace(/[^\d]/g, ""))}
              onBlur={() => handleBlur("nflDraftYear")}
            />
            {touched.nflDraftYear && form.nflDraftYear && !nflYearOk && (
              <div className="text-danger small mt-1 text-end">
                Enter a 4-digit year (e.g., 2026) or leave blank.
              </div>
            )}
          </div>

          <div className="col-12">
            <label className="fs-7 ps-1">
              CFL Draft Year
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="CFL DY"
              className={`form-control fs-7 alert-danger ps-1 ${borderIfInvalid("cflDraftYear")}`}
              value={form.cflDraftYear}
              onChange={(e) => handleChange("cflDraftYear", e.target.value.replace(/[^\d]/g, ""))}
              onBlur={() => handleBlur("cflDraftYear")}
            />
            {touched.cflDraftYear && form.cflDraftYear && !cflYearOk && (
              <div className="text-danger small mt-1 text-end">
                Enter a 4-digit year (e.g., 2026) or leave blank.
              </div>
            )}
          </div>
          <div className="col-12">
            <label className="fs-7 ps-1">Jersey</label>
            <input
              type="text"
              className="form-control fs-7 ps-1"
              placeholder="Jersey"
              value={form.jersey}
              onChange={(e) => handleChange("jersey", e.target.value)}
              onBlur={() => handleBlur("jersey")}
            />
          </div>

          <div className="flex-grow-1">
            <label className="fs-7 ps-1">Nationality</label>
            <select
              className="form-select fs-7 ps-1"
              value={form.impnip}
              onChange={(e) => handleChange("impnip", e.target.value)}
              onBlur={() => handleBlur("impnip")}
            >
              <option value="">Select Nationality</option>
              <option value="N">N - National</option>
              <option value="G">G - Global</option>
              <option value="V3">V3 - American 3 Year</option>
              <option value="V5">V5 - American 5 Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex gap-2 mt-3 justify-content-between">
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={handleReset}
          title="Reset the form"
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`btn btn-sm w-25 ${isFormValid ? "btn-success" : "btn-secondary"} text-white`}
          disabled={!isFormValid}
          aria-disabled={!isFormValid}
          title={!isFormValid ? "Fill all required fields to enable" : "Create"}
        >
          Create
        </button>
      </div>
    </form>
  );
};

export default NewPlayerForm;
